import { useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { MdOutlineClose, MdLabel, MdScore, MdBusinessCenter, MdArrowForward } from "react-icons/md";
import { listEntities } from "../api/entity";
import { listSubEntities } from "../api/subEntity";
import { useSelector } from "react-redux";

export default function ThresholdModal({ open, onClose, initial }) {
  const { accountId } = useSelector((state) => state.account);
  const [label, setLabel] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);
  const [entityId, setEntityId] = useState(null);
  const [subEntityId, setSubEntityId] = useState(null);

  const [entities, setEntities] = useState([]);
  const [subEntities, setSubEntities] = useState([]);

  // Load entity options
  useEffect(() => {
    listEntities().then(setEntities);
  }, []);

  // When entity changes, load sub-entities
  // useEffect(() => {
  //   if (accountId) {
  //     listSubEntities(accountId).then(setSubEntities);
  //   } else {
  //     setSubEntities([]);
  //   }
  // }, [accountId]);

  // On modal open/edit
  useEffect(() => {
    if (initial) {
      setLabel(initial.label || "");
      setMinScore(initial.min_score ?? 0);
      setMaxScore(initial.max_score ?? 100);
      setEntityId(initial.entity_id ?? null);
      setSubEntityId(initial.sub_entity_id ?? null);
    } else {
      setLabel("");
      setMinScore(0);
      setMaxScore(100);
      setEntityId(null);
      setSubEntityId(null);
    }
  }, [initial]);

  const handleSubmit = () => {
    onClose({
      label,
      min_score: Number(minScore),
      max_score: Number(maxScore),
      account_id: Number(accountId),
      sub_entity_id: subEntityId ? Number(subEntityId) : null,
    });
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => onClose(null)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all border border-gray-200">
                <div className="flex items-center justify-between mb-8">
                  <Dialog.Title className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {initial ? "✨ Edit Threshold" : "✨ New Threshold"}
                  </Dialog.Title>
                  <button
                    onClick={() => onClose(null)}
                    className="text-gray-400 hover:text-gray-500 transition-all hover:rotate-90 duration-300"
                  >
                    <MdOutlineClose size={22} />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Label Input */}
                  <div className="group">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <MdLabel className="text-blue-500" size={18} />
                      Risk Level
                    </label>
                    <input
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm 
                        focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all
                        group-hover:border-blue-400"
                      placeholder="Enter risk level (e.g. Low, Medium, High)..."
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                    />
                  </div>

                  {/* Score Range */}
                  <div className="group">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <MdScore className="text-purple-500" size={18} />
                      Score Range
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm 
                          focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all
                          group-hover:border-purple-400"
                        placeholder="Min"
                        value={minScore}
                        onChange={(e) => setMinScore(e.target.value)}
                      />
                      <MdArrowForward className="text-gray-400" size={20} />
                      <input
                        type="number"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm 
                          focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all
                          group-hover:border-purple-400"
                        placeholder="Max"
                        value={maxScore}
                        onChange={(e) => setMaxScore(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Entity Select */}
                  {/* <div className="group">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <MdBusinessCenter className="text-indigo-500" size={18} />
                      Entity
                    </label>
                    <select
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm 
                        focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all
                        group-hover:border-indigo-400"
                      value={entityId ?? ""}
                      onChange={(e) => setEntityId(e.target.value)}
                    >
                      <option value="">— Select Entity —</option>
                      {entities.map((e) => (
                        <option key={e.id} value={e.id}>{e.name}</option>
                      ))}
                    </select>
                  </div> */}

                  {/* Sub-Entity Select */}
                  {/* <div className="group">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <MdBusinessCenter className="text-indigo-500 opacity-75" size={18} />
                      Sub-Entity
                    </label>
                    <select
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm 
                        focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all
                        group-hover:border-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      value={subEntityId ?? ""}
                      onChange={(e) => setSubEntityId(e.target.value)}
                      disabled={!subEntities.length}
                    >
                      <option value="">— Select Sub-Entity (Optional) —</option>
                      {subEntities.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div> */}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-8">
                  <button
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 
                      rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/20 
                      transition-all hover:shadow-md"
                    onClick={() => onClose(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 
                      border border-transparent rounded-lg hover:from-blue-700 hover:to-purple-700 
                      focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all hover:shadow-md"
                    onClick={handleSubmit}
                  >
                    {initial ? "Save Changes" : "Create Threshold"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}